import { useEffect, useMemo } from 'react';
import {
  useUnleashContext,
  useUnleashClient,
} from '@unleash/proxy-client-react';
import type { IMutableContext } from '@unleash/proxy-client-react';
import { useUser } from '@/hooks/useUser';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { formatDate } from '../helpers/date';
import { captureException } from '@sentry/react';
import { useTracking } from 'react-tracking';
import type { UnleashEnvironment } from '../types';

export const useEvaluateUnleashContext = (): IMutableContext => {
  const { data: user } = useUser();
  const isMobile = useMobileDetect();

  return useMemo(
    () => ({
      userId: user.id,
      properties: {
        isNative: String(RUNTIME_ENV === 'native'),
        isMobile: String(isMobile),
        userBillingStatus: user.loggedIn ? user.billing.status : '',
        userRegistrationDate: user.loggedIn
          ? formatDate(user.registrationDate)
          : '',
        // PII is safe to use in Unleash as it is all handled within our internal systems.
        userCountryISO: user.countryISO,
        email: user.emailAddress,
      },
    }),
    [user, isMobile]
  );
};

type Props = {
  analyticsEventData: {
    environment: UnleashEnvironment;
  };
  prevContext: IMutableContext;
  setPrevContext: (c: IMutableContext) => void;
};

export const EvaluateContext = ({
  prevContext,
  setPrevContext,
  analyticsEventData,
}: Props) => {
  const updateContext = useUnleashContext();
  const context = useEvaluateUnleashContext();
  const client = useUnleashClient();
  const { trackEvent } = useTracking();

  useEffect(() => {
    const process = async () => {
      console.log('Unleash: context updated', JSON.stringify({ context }));
      await updateContext(context);
      // Tell Analytics (data reporting) what the user's current flag configurations are
      trackEvent({
        page: 'root',
        type: 'track',
        subject: 'unleash',
        action: 'evaluate',
        userFlags: client.getAllToggles(),
        ...analyticsEventData,
      });
    };
    try {
      if (JSON.stringify(prevContext) !== JSON.stringify(context)) {
        setPrevContext(context);
        process();
      }
    } catch (error) {
      captureException(error);
    }
  }, [prevContext, context, updateContext]);

  return null;
};
